// Version 5 dicembre ore 14:51 — Security hardened
import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { IoMdSend as Invia } from 'react-icons/io';
import { FaMicrophone, FaDotCircle, FaStop as Stop } from 'react-icons/fa';
import {
  BsThreeDotsVertical as DotsSettings,
  BsTrash3 as Cestino,
} from 'react-icons/bs';

import NuovaChat from './components/NuovaChat';
import { StateContext } from './components/StateContainer';
import FormGetInfoUser from './components/FormGetInfoUser/FormGetInfoUser';
import useSpeechRecognition from './components/hooks/UseSpeechRecognition';
import PoweredBy from './img/logoChat.png';
import './App.css';
import ModalDeleteChatHistory from './components/modals/ModalDeleteChatHistory';
import translations from './assets/translations/translation';

// ─────────────────────────────────────────────────────────────
// SECURITY UTILS
// ─────────────────────────────────────────────────────────────

/**
 * Restituisce una stringa sicura: rimuove caratteri di controllo e tronca.
 * NON usare per HTML — usare solo per testo pianoo / valori JSON.
 */
const sanitizeString = (value, maxLength = 2000) => {
  if (typeof value !== 'string') return '';
  // Rimuove null-byte e altri caratteri di controllo (eccetto \t \n \r)
  return value
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .slice(0, maxLength);
};

/**
 * Validazione URL: accetta solo http/https, blocca javascript: / data: / vbscript:
 */
const isSafeUrl = (url) => {
  if (typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Validazione colore esadecimale — evita CSS injection.
 */
const isSafeHexColor = (color) =>
  typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color);

/**
 * Legge dal localStorage con validazione e fallback.
 * Previene prototype pollution: verifica che il valore sia del tipo atteso.
 */
const safeLocalStorageGet = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? value : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Parsea JSON dal localStorage in modo sicuro.
 * Accetta solo Array o plain Object, blocca prototype pollution.
 */
const safeLocalStorageGetParsed = (key, fallback = []) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    // Blocca prototype pollution: non accettiamo oggetti con __proto__
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      !Object.prototype.hasOwnProperty.call(parsed, '__proto__') &&
      !Object.prototype.hasOwnProperty.call(parsed, 'constructor') &&
      !Object.prototype.hasOwnProperty.call(parsed, 'prototype')
    ) {
      return parsed;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

/**
 * Scrive nel localStorage con gestione errori.
 */
const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(
      key,
      typeof value === 'string' ? value : JSON.stringify(value),
    );
    return true;
  } catch {
    return false;
  }
};

/**
 * Merge sicuro di array di oggetti (sostituisce Object.assign({}, ...data)).
 * Previene prototype pollution filtrando chiavi pericolose.
 */
const safeMergeArray = (arr) => {
  if (!Array.isArray(arr)) return arr;
  const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
  const result = Object.create(null); // no prototype chain
  for (const item of arr) {
    if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
      for (const [k, v] of Object.entries(item)) {
        if (!BLOCKED_KEYS.has(k)) {
          result[k] = v;
        }
      }
    }
  }
  return result;
};

/**
 * Estrae e valida i parametri URL in modo sicuro.
 * Previene XSS da URL manipulation.
 */
const getSafeUrlParam = (urlParams, name, maxLength = 200) => {
  const value = urlParams.get(name);
  if (!value) return null;
  return sanitizeString(value, maxLength);
};

/**
 * Whitelist delle origini autorizzate per postMessage.
 * Aggiungere qui le origini del sito parent attendibile.
 * Se vuota, il controllo viene saltato (meno sicuro ma compatibile).
 */
const ALLOWED_ORIGINS = (() => {
  const raw = process.env.REACT_APP_ALLOWED_ORIGINS || '';
  return raw
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean);
})();

const isAllowedOrigin = (origin) => {
  if (ALLOWED_ORIGINS.length === 0) return true; // fallback permissivo
  return ALLOWED_ORIGINS.includes(origin);
};

// ─────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────

const Chat = ({ content }) => {
  const {
    message,
    setMessage,
    dettagliProgetto,
    setDettagliProgetto,
    showFormRequestContacts,
    setShowFormRequestContacts,
    leadCode,
    setStopAgent,
    setLeadCode,
    setShowFormContactFollowChat,
    loading,
    setLoading,
    setBotMessageSent,
    botMessageSent,
    cardBodyRef,
    conversationStarted,
    scriptContextualInformation,
    setScriptContextualInformation,
    setConversationStarted,
  } = useContext(StateContext);

  // 🕒 Per salvare la data del primo messaggio
  const [firstMessageDate, setFirstMessageDate] = useState(null);

  // 🖱️ Hover sugli elementi
  const [hoveredButton, setHoveredButton] = useState(null);
  const handleMouseEnter = useCallback((id) => setHoveredButton(id), []);
  const handleMouseLeave = useCallback(() => setHoveredButton(null), []);

  // 🎨 Colori dinamici — con validazione hex
  const hexToRGBA = (hex, alpha = 1) => {
    if (!isSafeHexColor(hex)) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // ── URL params con sanitizzazione ──
  // FIX: evita XSS da window.location.href manipolato
  const [urlProjectCode, locale, subConversation] = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      const code = getSafeUrlParam(params, 'projectCode', 100) || '';
      const loc = getSafeUrlParam(params, 'locale', 10) || 'it';
      const sub = getSafeUrlParam(params, 'subConversation', 200) || null;
      // Valida locale: solo lettere e trattino (es. "it", "en", "en-US")
      const safeLocale = /^[a-zA-Z]{2,5}(-[a-zA-Z]{2,4})?$/.test(loc)
        ? loc
        : 'it';
      return [code, safeLocale, sub];
    } catch {
      return ['', 'it', null];
    }
  }, []);

  const secondaryRGBA = useMemo(() => {
    const color = dettagliProgetto?.secondaryColor || '#000000';
    return hexToRGBA(color, 0.15);
  }, [dettagliProgetto]);

  const hexColorPerSVG = useMemo(() => {
    const color = dettagliProgetto?.secondaryColor || '#000000';
    return isSafeHexColor(color) ? color : '#000000';
  }, [dettagliProgetto]);

  // ── STATE ──
  const [error, setError] = useState('');
  const [totalChat, setTotalChat] = useState([]);
  const [disableUserMessageChat, setDisableUserMessageChat] = useState(false);
  const [projectCode, setProjectCode] = useState(urlProjectCode || '');
  const [conversationId, setConversationId] = useState(() =>
    safeLocalStorageGet(`conversationId-${urlProjectCode}`),
  );

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [caricamento, setCaricamento] = useState(false);
  const [reloadIframe, setReloadIframe] = useState(false);
  const [ip, setIP] = useState('');
  const [datiContestoSito, setDatiContestoSito] = useState('');

  const userLastMessageRef = useRef('');

  const { text, startListening, stopListening, isListening } =
    useSpeechRecognition();

  // ─────────────────────────────────────────────────────────────
  // UTILS
  // ─────────────────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    if (!cardBodyRef.current) return;
    const cardBody = cardBodyRef.current;
    setTimeout(() => {
      if (cardBody) {
        cardBody.scrollTo({
          top: cardBody.scrollHeight - cardBody.clientHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, [cardBodyRef]);

  const shouldShowContactForm = useCallback(
    (project) =>
      project.collectCustomerEmail &&
      project.lead?.collectLeadWhen === 'before-chat' &&
      safeLocalStorageGet(`showFormRequestContacts-${projectCode}`) !== 'false',
    [projectCode],
  );

  const shouldAskContactOnFirstMessage = useCallback(
    () =>
      totalChat.length === 0 &&
      dettagliProgetto?.collectCustomerEmail &&
      dettagliProgetto?.lead?.collectLeadWhen === 'first-response' &&
      safeLocalStorageGet(`showFormRequestContacts-${projectCode}`) !== 'false',
    [totalChat.length, dettagliProgetto, projectCode],
  );

  const getApiUrlByType = useCallback((type) => {
    // FIX: whitelist esplicita dei tipi validi — evita open redirect verso endpoint arbitrari
    const ALLOWED_TYPES = {
      conversational: `${process.env.REACT_APP_PYTHON_ENGINE}/knots-chatbot`,
      'mysql-agent': `${process.env.REACT_APP_PYTHON_ENGINE}/sql-agent`,
      default: `${process.env.REACT_APP_PYTHON_ENGINE}/chatbot`,
    };
    return ALLOWED_TYPES[type] || ALLOWED_TYPES['default'];
  }, []);

  const buildRequestOptions = useCallback(
    (messageText, codeLead, widget_clicked) => {
      // ── Gestione subConversation ──
      let finalConversationId = conversationId;
      let finalSubConversationId = null;

      if (subConversation) {
        const subConvArray = safeLocalStorageGetParsed(
          `subConversation-${urlProjectCode}`,
        );

        if (Array.isArray(subConvArray)) {
          const found = subConvArray.find(
            (item) =>
              typeof item === 'object' &&
              item !== null &&
              item.subConversationId === subConversation,
          );
          if (found) {
            finalSubConversationId = sanitizeString(
              found.subConversationId || '',
              200,
            );
            finalConversationId = sanitizeString(
              found.conversationId || conversationId || '',
              200,
            );
          }
        }
      }

      // FIX: sanitizza document.referrer prima di inviarlo al backend
      const safeReferrer = isSafeUrl(document.referrer)
        ? document.referrer.slice(0, 500)
        : '';

      // FIX: sanitizza currentUrl dal contesto script
      const safeCurrentUrl = isSafeUrl(scriptContextualInformation?.currentUrl)
        ? scriptContextualInformation.currentUrl.slice(0, 500)
        : '';

      // FIX: sanitizza datiContestoSito
      const safeDatiContesto = sanitizeString(
        scriptContextualInformation?.datiContestoSito || '',
        5000,
      );

      // FIX: legge localStorage con validazione tipo
      const currentNode = (() => {
        const v = safeLocalStorageGet(`currentNode-${urlProjectCode}`, '1');
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n >= 0 ? n : 1;
      })();

      const nextNode = (() => {
        const v = safeLocalStorageGet(`nextNodeProject-${urlProjectCode}`, '1');
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n >= 0 ? n : 1;
      })();

      const variables = safeLocalStorageGetParsed(
        `variables-${urlProjectCode}`,
        [],
      );

      const externalServiceKnowledge = safeLocalStorageGetParsed(
        `external_service_knoledge-${urlProjectCode}`,
        [],
      );

      const bodyData = {
        message: sanitizeString(messageText, 5000),
        referrer: safeReferrer,
        conversationId: sanitizeString(finalConversationId || '', 200),
        projectCode: sanitizeString(dettagliProgetto.code || '', 100),
        locale: locale,
        chatHistory: totalChat,
        leadCode: sanitizeString(leadCode || codeLead || '', 200),
        widgetClicked: !!widget_clicked,
        datiContestoSito: safeDatiContesto,
        trackingData: scriptContextualInformation?.trackingData ?? null,
        link: safeCurrentUrl,
        currentNode,
        nextNode,
        variables,
        external_service_knoledge: externalServiceKnowledge,
        subConversationId: finalSubConversationId
          ? sanitizeString(finalSubConversationId, 200)
          : null,
      };

      // Aggiungi ip solo se esiste, non è vuoto ed è un IPv4/IPv6 valido
      if (ip && /^[\d.:a-fA-F]+$/.test(ip) && ip.length <= 45) {
        bodyData.ip = ip;
      }

      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      };
    },
    [
      conversationId,
      datiContestoSito,
      dettagliProgetto,
      totalChat,
      leadCode,
      ip,
      scriptContextualInformation,
      urlProjectCode,
      subConversation,
      locale,
    ],
  );

  // FIX: safeMergeArray invece di Object.assign({}, ...data) — previene prototype pollution
  const handleBotResponse = useCallback(
    (data, conversation) => {
      const normalized = Array.isArray(data) ? safeMergeArray(data) : data;

      // Validazione minima: deve essere un oggetto plain
      if (typeof normalized !== 'object' || normalized === null) {
        console.error('handleBotResponse: risposta non valida', normalized);
        return;
      }

      if (normalized.faq_answer) {
        conversation.botMessage = {
          ...conversation.botMessage,
          text: sanitizeString(normalized.answer || '', 10000),
          faq_answer: true,
          faq_actions: Array.isArray(normalized.actions)
            ? normalized.actions
            : [],
          interactionCode: sanitizeString(
            normalized.interactionCode || '',
            200,
          ),
          show_thumbs: normalized.show_thumbs ?? true,
        };
        if (normalized.disable_user_text)
          setDisableUserMessageChat(!!normalized.disable_user_text);

        if (normalized.currentNodeProject) {
          const n = parseInt(normalized.currentNodeProject, 10);
          if (Number.isFinite(n) && n >= 0)
            safeLocalStorageSet(`currentNode-${urlProjectCode}`, String(n));
        }
        if (normalized.nextNodeProject) {
          const n = parseInt(normalized.nextNodeProject, 10);
          if (Number.isFinite(n) && n >= 0)
            safeLocalStorageSet(`nextNodeProject-${urlProjectCode}`, String(n));
        }
        if (normalized.var && normalized.var !== '[]') {
          const parsed = Array.isArray(normalized.var) ? normalized.var : [];
          safeLocalStorageSet(
            `variables-${urlProjectCode}`,
            JSON.stringify(parsed),
          );
        }
        if (
          normalized.external_service_knoledge &&
          normalized.external_service_knoledge !== '[]'
        ) {
          const parsed = Array.isArray(normalized.external_service_knoledge)
            ? normalized.external_service_knoledge
            : [];
          safeLocalStorageSet(
            `external_service_knoledge-${urlProjectCode}`,
            JSON.stringify(parsed),
          );
        }
      } else if (normalized.sql_graphic) {
        StopButtonClick();
        conversation.botMessage.sql_graphic = true;
        conversation.botMessage.dataGraphic = Array.isArray(normalized.data)
          ? normalized.data
          : [];
        conversation.botMessage.optionsGraphic = Array.isArray(
          normalized.options,
        )
          ? normalized.options
          : [];
      } else if (normalized.sql_table) {
        StopButtonClick();
        conversation.botMessage.sql_table = true;
        conversation.botMessage.sql_table_content = sanitizeString(
          normalized.sql_table_content || normalized.answer || '',
          20000,
        );
      }

      if (normalized.link_gestionale) {
        // FIX: valida link_gestionale prima di usarlo
        const link = normalized.link_gestionale;
        if (isSafeUrl(link)) {
          conversation.botMessage.link_gestionale = link;
        }
      }

      setTotalChat((prev) => [...prev.slice(0, -1), conversation]);
    },
    [urlProjectCode],
  );

  // ─────────────────────────────────────────────────────────────
  // MESSAGING
  // ─────────────────────────────────────────────────────────────
  const handleSendMessage = useCallback(
    async (messaggio, codeLead, widget_clicked = false) => {
      setDisableUserMessageChat(false);
      document.querySelectorAll('.faq-actions').forEach((el) => {
        el.classList.remove('animationDoneActionsFaq');
        el.classList.add('d-none');
      });

      setStopAgent(false);
      setConversationStarted(true);
      setBotMessageSent(true);
      setIsUserScrolling(false);
      setLoading(true);

      const userMessage = sanitizeString(
        (message.trim() || messaggio || '').trim(),
        5000,
      );
      if (!userMessage) return;

      const conversation = {
        userMessage: { message: userMessage, date: new Date() },
        botMessage: {
          text: '',
          date: new Date(),
          split: false,
          faq_answer: false,
        },
      };
      setTotalChat((prev) => [...prev, conversation]);
      setMessage('');

      try {
        const typeFromStorage = safeLocalStorageGet(
          `type-project-${projectCode}`,
          'default',
        );
        const apiURL = getApiUrlByType(typeFromStorage);

        const response = await fetch(
          apiURL,
          buildRequestOptions(userMessage, codeLead, widget_clicked),
        );

        // FIX: controlla Content-Type prima di fare .json()
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error('Risposta non JSON dal server');
        }

        const data = await response.json();

        if (!response.ok) throw new Error('Errore nella risposta');

        setBotMessageSent(false);
        setLoading(false);

        handleBotResponse(data, conversation);
      } catch (error) {
        console.error('Errore generale:', error);
        setLoading(false);
        setBotMessageSent(false);
        conversation.botMessage.text = 'Errore di rete o server. Riprova.';
        setTotalChat((prev) => [...prev.slice(0, -1), conversation]);
      }
    },
    [
      message,
      projectCode,
      buildRequestOptions,
      getApiUrlByType,
      handleBotResponse,
      setMessage,
    ],
  );

  const ControlsBeforeSendingMessage = useCallback(
    (messaggio, widget_clicked = false) => {
      setShowFormContactFollowChat(false);
      document.querySelectorAll('.faq-actions').forEach((el) => {
        el.classList.remove('animationDone');
        el.classList.add('d-none');
      });

      setMessage(messaggio);

      if (shouldAskContactOnFirstMessage()) {
        setShowFormRequestContacts(true);
      } else {
        handleSendMessage(messaggio, leadCode, widget_clicked);
      }
    },
    [shouldAskContactOnFirstMessage, handleSendMessage, leadCode],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !conversationStarted) {
        ControlsBeforeSendingMessage(event.target.value);
      }
    },
    [conversationStarted, ControlsBeforeSendingMessage],
  );

  const StopButtonClick = useCallback(() => {
    setStopAgent(true);
    setConversationStarted(false);
  }, [setStopAgent, setConversationStarted]);

  const CancellaCronologiaChat = useCallback(() => {
    const keys = [
      'conversationId',
      'currentNode',
      'nextNodeProject',
      'variables',
      'external_service_knoledge',
    ];

    const subConvKey = `subConversation-${projectCode}`;
    const storedSubConv = localStorage.getItem(subConvKey);

    if (storedSubConv) {
      try {
        const subConvArray = JSON.parse(storedSubConv);
        if (Array.isArray(subConvArray)) {
          const updatedArray = subConvArray.filter(
            (item) =>
              typeof item === 'object' &&
              item !== null &&
              item.subConversationId !== subConversation,
          );
          if (updatedArray.length === 0) {
            localStorage.removeItem(subConvKey);
          } else {
            safeLocalStorageSet(subConvKey, JSON.stringify(updatedArray));
          }
        }
      } catch (e) {
        console.error('Errore nella gestione di subConversation:', e);
      }
    }

    keys.forEach((key) => {
      localStorage.removeItem(`${key}-${projectCode}`);
    });

    setTimeout(() => {
      document.location.reload();
    }, 100);
  }, [projectCode, subConversation]);

  // ─────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (dettagliProgetto && dettagliProgetto.trackIp) {
      axios
        .get('https://api.ipify.org/?format=json')
        .then((res) => {
          // FIX: valida il formato IP prima di salvarlo
          const receivedIp = res.data?.ip;
          if (
            typeof receivedIp === 'string' &&
            /^[\d.:a-fA-F]+$/.test(receivedIp) &&
            receivedIp.length <= 45
          ) {
            setIP(receivedIp);
          }
        })
        .catch(console.error);
    } else {
      setIP('');
    }

    window.parent.postMessage({ action: 'tailorGptReady' }, '*');

    // FIX: verifica origin del messaggio per prevenire attacchi postMessage
    const handleMessage = (event) => {
      if (!event.data || event.data.type?.startsWith('webpack')) return;

      // FIX: controlla l'origine del messaggio
      if (!isAllowedOrigin(event.origin)) {
        console.warn(
          `postMessage ignorato da origine non autorizzata: ${event.origin}`,
        );
        return;
      }

      if (
        event.data?.action === 'cookieWebSite' ||
        event.data?.action === 'reload'
      ) {
        // FIX: sanitizza tutti i campi provenienti dal parent
        const safeCurrentUrl = isSafeUrl(event.data.currentUrl)
          ? event.data.currentUrl.slice(0, 500)
          : '';

        const safeDatiContesto = sanitizeString(
          event.data.datiContestoSito || '',
          5000,
        );

        const safeLogo = isSafeUrl(event.data.logo)
          ? event.data.logo.slice(0, 500)
          : '';

        const safeAssistantName = sanitizeString(
          event.data.assistantName || '',
          200,
        );

        setScriptContextualInformation({
          currentUrl: safeCurrentUrl,
          datiContestoSito: safeDatiContesto,
          logo: safeLogo,
          assistantName: safeAssistantName,
          // trackingData: accettato as-is ma non renderizzato come HTML
          trackingData: event.data.trackingData ?? null,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    let storedConvId = safeLocalStorageGet(`conversationId-${urlProjectCode}`);
    if (!storedConvId) {
      storedConvId = `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
      safeLocalStorageSet(`conversationId-${urlProjectCode}`, storedConvId);
    }

    if (subConversation) {
      const subConversations = safeLocalStorageGetParsed(
        `subConversation-${urlProjectCode}`,
        [],
      );
      const exists =
        Array.isArray(subConversations) &&
        subConversations.some(
          (item) =>
            typeof item === 'object' &&
            item !== null &&
            item.subConversationId === subConversation,
        );

      if (!exists) {
        const updated = Array.isArray(subConversations)
          ? [
              ...subConversations,
              {
                conversationId: storedConvId,
                subConversationId: subConversation,
              },
            ]
          : [
              {
                conversationId: storedConvId,
                subConversationId: subConversation,
              },
            ];
        safeLocalStorageSet(
          `subConversation-${urlProjectCode}`,
          JSON.stringify(updated),
        );
      }
    }

    setConversationId(storedConvId);
  }, [urlProjectCode, subConversation]);

  useEffect(() => {
    let conversationCode = null;

    const subConvKey = `subConversation-${urlProjectCode}`;
    const storedSubConv = safeLocalStorageGet(subConvKey);

    if (storedSubConv) {
      try {
        const subConvArray = JSON.parse(storedSubConv);
        if (Array.isArray(subConvArray)) {
          const found = subConvArray.find(
            (item) =>
              typeof item === 'object' &&
              item !== null &&
              item.subConversationId === subConversation,
          );
          if (found?.conversationId) {
            conversationCode = sanitizeString(found.conversationId, 200);
          }
        }
      } catch (err) {
        console.error('Errore parsing subConversation:', err);
      }
    }

    if (!conversationCode) {
      conversationCode = safeLocalStorageGet(
        `conversationId-${urlProjectCode}`,
      );
    }

    axios
      .post(`${process.env.REACT_APP_BASE_URL}/public/conversation/detail`, {
        projectCode: sanitizeString(urlProjectCode, 100),
        subConversationCode: subConversation
          ? sanitizeString(subConversation, 200)
          : null,
        conversationCode: sanitizeString(conversationCode || '', 200),
      })
      .then(({ data }) => {
        if (!data.response) return;

        const conversation = data.response.map((msg) => ({
          userMessage: {
            message: sanitizeString(msg.userText || '', 5000),
            date: msg.date?.date,
          },
          botMessage: {
            text: sanitizeString(msg.botText?.trim() || '', 10000),
            date: msg.date?.date,
            split: true,
            faq_answer: false,
          },
        }));

        setTotalChat(conversation);
      })
      .catch(console.error);
  }, [urlProjectCode, subConversation]);

  useEffect(() => {
    setCaricamento(true);

    axios
      .post(`${process.env.REACT_APP_BASE_URL}/public/project/detail`, {
        projectCode: sanitizeString(projectCode, 100),
        locale,
      })
      .then(({ data }) => {
        setError('');
        setReloadIframe(false);
        setCaricamento(false);
        setDettagliProgetto(data.response);
        safeLocalStorageSet(
          `type-project-${projectCode}`,
          sanitizeString(data.response.type || 'default', 50),
        );
        const dettagli = data.response;

        const STORAGE_KEY = `tailorGPT_projectData_${projectCode}`;
        const dataToSave = {
          initialMessage: sanitizeString(dettagli.initialMessage || '', 1000),
          primaryColor: isSafeHexColor(dettagli.primaryColor)
            ? dettagli.primaryColor
            : '#000000',
          language: sanitizeString(dettagli.language || '', 20),
          assistantName: sanitizeString(dettagli.assistantName || '', 200),
          chatbotIcon: isSafeUrl(dettagli.chatbotIcon)
            ? dettagli.chatbotIcon
            : '',
          chatBubbleAlignement: sanitizeString(
            dettagli.chatBubbleAlignement || '',
            20,
          ),
          createdAt: new Date().toISOString(),
        };
        safeLocalStorageSet(STORAGE_KEY, dataToSave);

        if (shouldShowContactForm(data.response))
          setShowFormRequestContacts(true);
        scrollToBottom();
      })
      .catch((err) => setError(err));
  }, [
    projectCode,
    reloadIframe,
    shouldShowContactForm,
    scrollToBottom,
    setDettagliProgetto,
  ]);

  useEffect(() => {
    const cardBody = cardBodyRef.current;
    const onScroll = () => {
      const nearBottom =
        cardBody.scrollHeight - cardBody.scrollTop - cardBody.clientHeight < 50;
      setIsUserScrolling(!nearBottom);
    };
    cardBody.addEventListener('scroll', onScroll);
    return () => cardBody.removeEventListener('scroll', onScroll);
  }, [cardBodyRef]);

  useEffect(() => {
    if (!isUserScrolling) scrollToBottom();
  }, [botMessageSent, isUserScrolling, scrollToBottom]);

  if (dettagliProgetto)
    document.title = sanitizeString(
      dettagliProgetto.title || 'Ai Assistant',
      100,
    );

  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(!isOpen);

  // FIX: colore secondario validato per uso inline
  const safeSecondaryColor = isSafeHexColor(dettagliProgetto?.secondaryColor)
    ? dettagliProgetto.secondaryColor
    : '#000000';

  // FIX: URL privacy validato
  const safePrivacyUrl =
    dettagliProgetto?.privacy && isSafeUrl(dettagliProgetto.privacy)
      ? dettagliProgetto.privacy
      : null;

  return (
    <div className="App">
      <div id="layout-wrapper">
        <div className="main-content m-0 p-0 h-100">
          <div className="page-content p-0">
            {dettagliProgetto &&
            dettagliProgetto.lead &&
            dettagliProgetto.collectCustomerEmail &&
            dettagliProgetto.lead.collectLeadWhen !== 'follow-conversation' ? (
              <FormGetInfoUser
                locale={locale}
                handleSendMessage={handleSendMessage}
                message={message}
                progetto={dettagliProgetto}
                conversationId={conversationId}
                setConversationId={setConversationId}
                setMessage={setMessage}
              />
            ) : (
              ''
            )}

            <div className="container-fluid">
              <div className="row">
                <div className="col-12 px-0 position-fixed fixed-top">
                  <div className="w-100 user-chat mt-sm-0">
                    <div className="card" style={{ border: 0 }}>
                      <div
                        ref={cardBodyRef}
                        className="card-body"
                        id="cardBodyChat"
                        dir="ltr"
                        style={{
                          overflowX: 'hidden',
                          overflowY: 'scroll',
                          border: 0,
                          position: 'relative',
                        }}
                      >
                        {caricamento ? (
                          <div className="form-outline mt-5">
                            <div className="loading-dots">
                              <div className="loading-dots--dot"></div>
                              <div className="loading-dots--dot"></div>
                              <div className="loading-dots--dot"></div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="chat-conversation py-3">
                              <ul
                                className="list-unstyled mb-0 chat-conversation-message"
                                data-simplebar
                                style={{ fontSize: '0.875rem' }}
                                id="ulList"
                              >
                                <>
                                  <NuovaChat
                                    progetto={dettagliProgetto}
                                    chat={totalChat}
                                    locale={locale}
                                    firstMessageDate={firstMessageDate}
                                    caricamento={caricamento}
                                    totalChat={totalChat}
                                    ControlsBeforeSendingMessage={
                                      ControlsBeforeSendingMessage
                                    }
                                    setIsUserScrolling={setIsUserScrolling}
                                    isUserScrolling={isUserScrolling}
                                    setLoading={setLoading}
                                    message={message}
                                    userLastMessageRef={userLastMessageRef}
                                    setMessage={setMessage}
                                    setTotalChat={setTotalChat}
                                    conversationId={conversationId}
                                    setLeadCode={setLeadCode}
                                  />
                                </>

                                {dettagliProgetto &&
                                dettagliProgetto.lead &&
                                dettagliProgetto.collectCustomerEmail &&
                                dettagliProgetto.lead.collectLeadWhen &&
                                dettagliProgetto.lead.collectLeadWhen ===
                                  'follow-conversation' ? (
                                  <>
                                    <FormGetInfoUser
                                      locale={locale}
                                      handleSendMessage={handleSendMessage}
                                      message={message}
                                      progetto={dettagliProgetto}
                                      conversationId={conversationId}
                                      setConversationId={setConversationId}
                                      setMessage={setMessage}
                                    />
                                    <li style={{ height: '70px' }}></li>
                                  </>
                                ) : (
                                  ''
                                )}
                                <li style={{ height: '60px' }}></li>
                              </ul>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="divider"></div>

                      <div
                        className="position-absolute w-100 text-center p-3"
                        style={{ bottom: '0' }}
                      >
                        {dettagliProgetto && dettagliProgetto.predefinedQueries
                          ? dettagliProgetto.predefinedQueries.map(
                              (query, index) => (
                                <button
                                  key={index}
                                  className="btn div-advice-buttons mx-2 font-size-14"
                                  onClick={() =>
                                    ControlsBeforeSendingMessage(query[1])
                                  }
                                  onMouseEnter={() => handleMouseEnter(index)}
                                  onMouseLeave={handleMouseLeave}
                                  style={{
                                    backgroundColor:
                                      hoveredButton === index
                                        ? safeSecondaryColor
                                        : 'white',
                                  }}
                                >
                                  <p className="m-0">{query[0]}</p>
                                </button>
                              ),
                            )
                          : ''}
                      </div>

                      {caricamento ? (
                        ''
                      ) : (
                        <div
                          className={`py-5 card-footer chat-input-section d-flex flex-fill align-items-start w-100 justify-content-center bg-white footer-chat position-fixed fixed-bottom`}
                          style={{ zIndex: '999' }}
                        >
                          <div className="footer-row row w-100 d-flex justify-content-center m-0">
                            <div
                              style={{
                                backgroundColor: `${secondaryRGBA}`,
                                height: '50px',
                                borderTop: dettagliProgetto
                                  ? `1px ${safeSecondaryColor} solid`
                                  : '',
                                borderBottom: dettagliProgetto
                                  ? `1px ${safeSecondaryColor} solid`
                                  : '',
                              }}
                              className="w-100 d-flex justify-content-center p-0 align-items-center m-0 px-4"
                            >
                              <div className="w-100">
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control search-bar w-100 bg-transparent border-0 shadow-none px-0"
                                    style={{ outline: 'none' }}
                                    placeholder={`${translations[`sendAMessage-${locale}`]}`}
                                    value={message}
                                    onChange={(event) =>
                                      setMessage(event.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    disabled={disableUserMessageChat}
                                    maxLength={5000}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck="false"
                                  />
                                </div>
                              </div>

                              {/* Desktop */}
                              <div className="col-auto d-flex align-items-center d-none d-md-block">
                                {!conversationStarted ? (
                                  <button
                                    type="button"
                                    className="btn chat-send w-md waves-effect waves-light d-flex align-items-center justify-content-center border-0"
                                    style={{
                                      backgroundColor: dettagliProgetto
                                        ? safeSecondaryColor
                                        : '',
                                      color: 'white',
                                      border: '1px grey solid',
                                    }}
                                    onClick={() =>
                                      ControlsBeforeSendingMessage(message)
                                    }
                                  >
                                    <span
                                      className="d-none d-sm-inline-block me-2 text-white"
                                      style={{ color: 'white' }}
                                    >
                                      {translations[`send-${locale}`]}
                                    </span>
                                    <Invia fill="white" stroke="white" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn chat-send w-md waves-effect waves-light d-flex align-items-center justify-content-center border-0"
                                    style={{
                                      backgroundColor: dettagliProgetto
                                        ? safeSecondaryColor
                                        : '',
                                      color: 'white',
                                      border: '1px grey solid',
                                    }}
                                    onClick={StopButtonClick}
                                  >
                                    <span className="d-none d-sm-inline-block me-2">
                                      Stop
                                    </span>
                                    <Stop fill="white" stroke="white" />
                                  </button>
                                )}
                              </div>

                              {/* Mobile */}
                              <div className="col-auto d-flex align-items-center d-block d-md-none">
                                {!conversationStarted ? (
                                  <button
                                    type="button"
                                    className="btn chat-send waves-effect waves-light d-flex align-items-center justify-content-center border-0 rounded-circle p-0 fs-4"
                                    style={{
                                      backgroundColor: dettagliProgetto
                                        ? safeSecondaryColor
                                        : '',
                                      height: '40px',
                                      width: '40px',
                                      color: 'white',
                                      border: '1px grey solid',
                                    }}
                                    onClick={() =>
                                      ControlsBeforeSendingMessage(message)
                                    }
                                  >
                                    <Invia fill="white" stroke="white" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn chat-send waves-effect waves-light d-flex align-items-center justify-content-center border-0 rounded-circle p-0 fs-4"
                                    style={{
                                      backgroundColor: dettagliProgetto
                                        ? safeSecondaryColor
                                        : '',
                                      height: '40px',
                                      width: '40px',
                                      color: 'white',
                                      border: '1px grey solid',
                                    }}
                                    onClick={StopButtonClick}
                                  >
                                    <Stop fill="white" stroke="white" />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="w-100 d-flex align-items-start justify-content-between my-2 px-4 mb-4">
                              <div className="d-flex gap-5 align-items-start">
                                {dettagliProgetto &&
                                dettagliProgetto.hideTailorGPTLogo ? (
                                  ''
                                ) : (
                                  <div className="my-1">
                                    <a
                                      href="https://tailorgpt.ai/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-decoration-none d-flex"
                                    >
                                      <p
                                        className="font-size-11 m-0"
                                        style={{ color: '#535353' }}
                                      >
                                        Powered By
                                      </p>
                                      <div className="logo-tailor-container d-flex align-items-center gap-2 position-relative overflow-hidden">
                                        <div className="shine"></div>
                                        <img
                                          style={{ width: 'auto' }}
                                          src={`${PoweredBy}`}
                                          alt="Tailor GPT - your own generative AI"
                                          className="img-fulgeas-chat"
                                        />
                                      </div>
                                    </a>
                                  </div>
                                )}

                                {/* FIX: URL privacy validato con isSafeUrl */}
                                {safePrivacyUrl ? (
                                  <div className="my-1 pb-4 d-flex align-items-center">
                                    <a
                                      className="font-size-11"
                                      style={{ color: '#535353' }}
                                      href={safePrivacyUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Privacy
                                    </a>
                                  </div>
                                ) : (
                                  ''
                                )}
                              </div>

                              <div className="my-1 pb-4 d-flex align-items-center">
                                <a
                                  className="font-size-15"
                                  style={{ color: '#535353' }}
                                  type="button"
                                  data-bs-toggle="offcanvas"
                                  data-bs-target="#offcanvasWithBothOptions"
                                  aria-controls="offcanvasWithBothOptions"
                                >
                                  <DotsSettings />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Offcanvas */}
                    <div
                      className="offcanvas offcanvas-start w-100 p-0 m-0"
                      data-bs-scroll="true"
                      tabIndex="-1"
                      id="offcanvasWithBothOptions"
                      aria-labelledby="offcanvasWithBothOptionsLabel"
                    >
                      <div className="offcanvas-header">
                        <h5
                          className="offcanvas-title"
                          id="offcanvasWithBothOptionsLabel"
                        >
                          {translations[`chatSettings-${locale}`]}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="offcanvas"
                          id="close-button-cookies-offcanvas"
                          aria-label="Close"
                        ></button>
                      </div>
                      <div className="offcanvas-body">
                        <button
                          className="text-danger d-flex align-items-center gap-2 bg-transparent"
                          onClick={openModal}
                        >
                          <Cestino />
                          {translations[`clearHistory-${locale}`]}
                        </button>
                      </div>
                    </div>
                    {/* Fine Offcanvas */}

                    {isOpen && (
                      <ModalDeleteChatHistory
                        dettagliProgetto={dettagliProgetto}
                        locale={locale}
                        CancellaCronologiaChat={CancellaCronologiaChat}
                        setIsOpen={setIsOpen}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
